import { useEffect, useMemo, useState, useRef } from "react";
import {
  generateDefaultTheme,
  generateDSNMTheme,
  generateCustomTheme,
  type ThemeReturn,
} from "src/mock/colorTheme";
import { useSelector } from "src/redux/hooks";
import { type CustomThemeData } from "src/interfaces/customTheme";

export interface UseThemeReturn extends ThemeReturn {
  isDarkMode: boolean;
  isLoadingCustomTheme: boolean;
  customThemeError: string | null;
}

// Simple cache to avoid re-fetching the same theme URLs
const themeCache = new Map<string, CustomThemeData>();

/**
 * Custom hook to generate and manage the application's theme based on user preferences.
 *
 * @returns {UseThemeReturn} The generated color theme object with loading and error states.
 */
export const useTheme = (): UseThemeReturn => {
  const { theme, customThemeUrl, isDarkMode } = useSelector(
    state => state.preferencesSlice,
  );

  const [customThemeData, setCustomThemeData] =
    useState<CustomThemeData | null>(null);
  const [isLoadingCustomTheme, setIsLoadingCustomTheme] = useState(false);
  const [customThemeError, setCustomThemeError] = useState<string | null>(null);

  // useRef to track mounted status and avoid setting state after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (theme !== "Custom" || !customThemeUrl) {
      setCustomThemeData(null);
      setCustomThemeError(null);
      setIsLoadingCustomTheme(false);
      return;
    }

    // Check cache first
    const cachedTheme = themeCache.get(customThemeUrl);
    if (cachedTheme) {
      setCustomThemeData(cachedTheme);
      setCustomThemeError(null);
      setIsLoadingCustomTheme(false);
      return;
    }

    setIsLoadingCustomTheme(true);
    setCustomThemeError(null);

    void (async () => {
      try {
        const themeModule = await import(/* @vite-ignore */ customThemeUrl);
        const themeData = themeModule.default as CustomThemeData;

        if (isMountedRef.current) {
          themeCache.set(customThemeUrl, themeData);
          setCustomThemeData(themeData);
          setCustomThemeError(null);
          setIsLoadingCustomTheme(false);
        }
      } catch (error: unknown) {
        if (isMountedRef.current) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load custom theme";
          console.error("Custom theme import error:", errorMessage);
          setCustomThemeError(errorMessage);
          setCustomThemeData(null);
          setIsLoadingCustomTheme(false);
        }
      }
    })();
  }, [theme, customThemeUrl]);

  const themeData = useMemo(() => {
    switch (theme) {
      case "DSNM":
        return generateDSNMTheme(isDarkMode);
      case "Custom":
        if (isLoadingCustomTheme) {
          return generateDefaultTheme(isDarkMode);
        }
        if (customThemeError ?? !customThemeData) {
          console.warn(
            "Using default theme due to custom theme error:",
            customThemeError,
          );
          return generateDefaultTheme(isDarkMode);
        }
        return generateCustomTheme(isDarkMode, customThemeData);
      default:
        return generateDefaultTheme(isDarkMode);
    }
  }, [
    theme,
    isDarkMode,
    customThemeData,
    isLoadingCustomTheme,
    customThemeError,
  ]);

  useEffect(() => {
    document.body.classList.toggle("picker_dark", isDarkMode);
  }, [isDarkMode]);

  const result: UseThemeReturn = {
    ...themeData,
    isDarkMode,
    isLoadingCustomTheme,
    customThemeError,
  };
  return result;
};
